import { useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type UiType = 'comment' | 'reply' | 'logbook-record' | 'task';

export const getQueryParamsForScrollAndFocus = ({
  uiType,
  id,
}: {
  uiType: UiType;
  id: string;
}) => {
  return { focus: `${uiType}-${id}` };
};

export const useGetFocusId = ({ uiType }: { uiType: UiType }) => {
  const searchParams = useSearch({ strict: false });

  const focusParam = searchParams.focus;

  const [, id] = focusParam?.split(`${uiType}-`) ?? [];

  return { id: id as string };
};

const HAS_CLICKED_ANYWHERE_DEFAULT_VALUE = false;

export const useScrollAndFocus = ({
  uiType,
  id,
}: {
  uiType: UiType;
  id: string;
}) => {
  const searchParams = useSearch({ strict: false });
  const focusParam = searchParams.focus;
  const idToFocus = focusParam?.split(`${uiType}-`)[1];
  const shouldFocus = idToFocus === id;

  const ref = useRef<HTMLDivElement>(null);

  const [hasClickedAnywhere, setHasClickedAnywhere] = useState(
    HAS_CLICKED_ANYWHERE_DEFAULT_VALUE,
  );

  useEffect(() => {
    if (shouldFocus && ref.current) {
      // Check if we're in inbox layout by looking for the inbox container
      const inboxContainer = document.querySelector('#inbox-layout');
      const dealScrollContainer = document.querySelector(
        '#deal-scroll-container',
      );

      if (inboxContainer && dealScrollContainer) {
        // We're in inbox layout - scroll within the deal container instead of viewport
        const containerRect = dealScrollContainer.getBoundingClientRect();
        const elementRect = ref.current.getBoundingClientRect();

        // Calculate the scroll position needed to center the element within the container
        const scrollTop =
          dealScrollContainer.scrollTop +
          (elementRect.top - containerRect.top) -
          containerRect.height / 2 +
          elementRect.height / 2;

        dealScrollContainer.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });
      } else {
        // Normal behavior for non-inbox layouts
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      setTimeout(() => {
        setHasClickedAnywhere(HAS_CLICKED_ANYWHERE_DEFAULT_VALUE);
      }, 0);
    }
  }, [shouldFocus, ref.current]);

  const handleClickAnywhere = useCallback(() => {
    setHasClickedAnywhere(true);
  }, [ref.current]);

  useEffect(() => {
    if (!shouldFocus) {
      return;
    }

    document.addEventListener('click', handleClickAnywhere);

    return () => {
      document.removeEventListener('click', handleClickAnywhere);
    };
  }, [handleClickAnywhere, shouldFocus]);

  const shouldShowFocus = useMemo(
    () => shouldFocus && !hasClickedAnywhere,
    [shouldFocus, hasClickedAnywhere],
  );

  return {
    shouldShowFocus,
    focusRef: ref,
  };
};
